import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarProps,
  SidebarSection,
  ThemeSwitch,
} from "../../sandbox/components";
import { ToggleContext } from "../../contexts/toggleContext";
import { AdvancedMode } from "../AdvancedMode";
import { useAccountAccess } from "../../hooks/useAccountAccess";
import { useTheme } from "../../hooks/useTheme";
import styles from "./styles.module.scss";

// Navigation structure — pure data. The DS <Sidebar/> owns all markup/styles.
// Grouped like the ASA platform nav; the "New order" CTA closes the Orders group.
const SECTIONS: SidebarSection[] = [
  {
    key: "dashboard",
    title: "Dashboard",
    items: [
      {
        key: "overview",
        label: "Overview",
        icon: "overview",
        to: "/dashboard",
      },
      { key: "balance", label: "Balance", icon: "balance", to: "/balance" },
      { key: "activity", label: "Logs", icon: "activity", to: "/activity" },
    ],
  },
  {
    key: "applications",
    title: "Applications",
    items: [
      { key: "apps", label: "Apps", icon: "apps", to: "/my-apps" },
      {
        key: "app-insights",
        label: "App Insights",
        icon: "app-insights",
        to: "/app-overview",
      },
    ],
  },
  {
    key: "orders",
    title: "Orders",
    items: [
      { key: "orders", label: "My orders", icon: "orders", to: "/orders" },
      {
        key: "keyword-planner",
        label: "Planner",
        icon: "planner",
        to: "/keyword-planner",
      },
    ],
  },
];

const NAV = SECTIONS.flatMap((section) => section.items);

// app theme uses light|auto|dark; the DS switch uses light|system|dark
const THEME_TO_DS = { light: "light", auto: "system", dark: "dark" } as const;
const THEME_FROM_DS = { light: "light", system: "auto", dark: "dark" } as const;

/**
 * App container: wires the design-system Sidebar to routing, toggle and modal.
 * On a phone it is the drawer the top bar opens, and the theme switch the
 * top bar has no room for sits at its foot.
 */
const AppSidebar: React.FC<Pick<SidebarProps, "drawer">> = ({ drawer }) => {
  // ToggleContext semantics: isOpen === true means the menu is COLLAPSED.
  const { isOpen, toggle } = useContext(ToggleContext);
  const location = useLocation();
  const navigate = useNavigate();
  // A viewer looks at orders but does not place them.
  const { can } = useAccountAccess();
  const { theme, setTheme } = useTheme();

  const activeKey = NAV.find(
    (i) => i.to && location.pathname.startsWith(i.to),
  )?.key;

  return (
    <>
      <Sidebar
        sections={SECTIONS}
        activeKey={activeKey}
        opened={!isOpen}
        onToggle={toggle}
        onItemSelect={(key) => {
          const item = NAV.find((i) => i.key === key);
          if (item?.to) navigate(item.to);
        }}
        cta={
          can("orders:create")
            ? {
                label: "New order",
                // The order type is picked on the page itself.
                onClick: () => navigate("/new-order"),
                active: location.pathname.startsWith("/new-order"),
              }
            : undefined
        }
        footer={
          drawer ? (
            <div className={styles.drawerFooter}>
              <span className={styles.drawerFooterLabel}>Theme</span>
              <ThemeSwitch
                theme={THEME_TO_DS[theme as keyof typeof THEME_TO_DS]}
                onThemeChange={(mode) => setTheme(THEME_FROM_DS[mode])}
              />
            </div>
          ) : (
            <AdvancedMode open={!isOpen} />
          )
        }
        drawer={drawer}
      />
    </>
  );
};

export default AppSidebar;
